<template>
  <div class="relative">
    <mia-feature-input
      v-model="query"
      placeholder="Suchbegriff eingeben..."
      autofocus
      @input="search"
      @focus="onFocus"
    />

    <div
      v-if="results.length > 0"
      class="absolute top-0 z-20 w-full p-3 pt-0 mt-8 overflow-x-hidden overflow-y-auto bg-white border border-t-0 border-gray-500 shadow-xl"
      style="max-height: 400px;"
    >
      <div
        v-for="page in results"
        :key="page.label"
      >
        <span
          class="block pt-5 pb-2 text-lg tracking-wider text-gray-600 border-b border-gray-400"
          v-text="page.label"
        />
        <div
          v-for="section in page.children"
          :key="section.label"
          class="flex my-1"
        >
          <div class="w-48 p-2 text-right text-gray-500 border-r border-gray-400">
            <vue-text-highlight :queries="queries">
              {{ section.label }}
            </vue-text-highlight>
          </div>
          <div class="p-2">
            <router-link
              :to="`/cars/${car.id}/${page.link}#${section.hash}`"
              class="block mb-1"
              @click.native="reset"
            >
              <vue-text-highlight
                :queries="queries"
                highlight-class="bg-orange-mia"
              >
                {{ section.label }}
              </vue-text-highlight>
            </router-link>

            <router-link
              v-for="field in section.children"
              :key="field.label"
              :to="`/cars/${car.id}/${page.link}#${section.hash}-${field.hash}`"
              class="block mb-1"
              @click.native="reset"
            >
              <vue-text-highlight
                :queries="queries"
                highlight-class="bg-orange-mia"
              >
                {{ field.label }}
              </vue-text-highlight>
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Fuse from 'fuse.js'
import TextHighlight from 'vue-text-highlight'
import FeatureInput from '@/components/cars/edit/FeatureInput.vue'

export default {
  name: 'MiaCarEditQuickSearch',

  components: {
    'mia-feature-input': FeatureInput,
    'vue-text-highlight': TextHighlight
  },

  data() {
    return {
      searchIndex: [],
      results: [],
      query: ''
    }
  },

  computed: {
    car() {
      return this.$store.state.car.car
    },

    queries() {
      return this.query.split(' ').map(part => part.toLowerCase())
    }
  },

  created() {
    this.createSearchIndex()
  },

  methods: {
    createSearchIndex() {
      // Stammdaten
      this.searchIndex.push({
        type: 'page',
        label: 'Stammdaten',
        link: 'stammdaten',
        parent: null
      })

      // Stammdaten - Basisdaten
      this.searchIndex.push({
        type: 'section',
        label: 'Basisdaten',
        hash: 'basisdaten',
        parent: 'Stammdaten'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'FIN',
        hash: 'fin',
        parent: 'Basisdaten'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Kommissionsnummer',
        hash: 'kommissionsnummer',
        parent: 'Basisdaten'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Kategorie',
        hash: 'kategorie',
        parent: 'Basisdaten'
      })

      // Stammdaten - Fahrzeug
      this.searchIndex.push({
        type: 'section',
        label: 'Fahrzeug',
        hash: 'fahrzeug',
        parent: 'Stammdaten'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Hersteller',
        hash: 'hersteller',
        parent: 'Fahrzeug'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Modell',
        hash: 'modell',
        parent: 'Fahrzeug'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Modellcode',
        hash: 'modellcode',
        parent: 'Fahrzeug'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Modellreihe',
        hash: 'modellreihe',
        parent: 'Fahrzeug'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Ausstattungslinie',
        hash: 'ausstattungslinie',
        parent: 'Fahrzeug'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Leistung',
        hash: 'leistung',
        parent: 'Fahrzeug'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Bereifung',
        hash: 'bereifung',
        parent: 'Fahrzeug'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Händlerfreigabe',
        hash: 'haendlerfreigabe',
        parent: 'Fahrzeug'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Kennzeichen',
        hash: 'kennzeichen',
        parent: 'Fahrzeug'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Kilometerstand',
        hash: 'kilometerstand',
        parent: 'Fahrzeug'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Anzahl Vorbesitzer',
        hash: 'anzahl-vorbesitzer',
        parent: 'Fahrzeug'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Erstzulassung',
        hash: 'erstzulassung',
        parent: 'Fahrzeug'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Erstzulassung auf Kunden',
        hash: 'erstzulassung-kunde',
        parent: 'Fahrzeug'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Bestelldatum',
        hash: 'bestelldatum',
        parent: 'Fahrzeug'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Abmeldung',
        hash: 'abmeldung',
        parent: 'Fahrzeug'
      })

      // Stammdaten - Bestand
      this.searchIndex.push({
        type: 'section',
        label: 'Bestand',
        hash: 'bestand',
        parent: 'Stammdaten'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Lagerplatz',
        hash: 'lagerplatz',
        parent: 'Bestand'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Brieflagerplatz',
        hash: 'brieflagerplatz',
        parent: 'Bestand'
      })

      this.searchIndex.push({
        type: 'section',
        label: 'Zulassungen',
        hash: 'zulassungen',
        parent: 'Stammdaten'
      })

      this.searchIndex.push({
        type: 'section',
        label: 'Produktionsdaten',
        hash: 'produktionsdaten',
        parent: 'Stammdaten'
      })

      this.searchIndex.push({
        type: 'section',
        label: 'Fahrzeugbewegungen',
        hash: 'fahrzeugbewegungen',
        parent: 'Stammdaten'
      })

      this.searchIndex.push({
        type: 'section',
        label: 'Dokumente',
        hash: 'dokumente',
        parent: 'Stammdaten'
      })

      // Börsendaten & Bilder
      this.searchIndex.push({
        type: 'page',
        label: 'Börsendaten & Bilder',
        link: 'boersendaten',
        parent: null
      })

      // Börsendaten & Bilder - Angebot
      this.searchIndex.push({
        type: 'section',
        label: 'Angebot',
        hash: 'angebot',
        parent: 'Börsendaten & Bilder'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Handelsspanne',
        hash: 'handelsspanne',
        parent: 'Angebot'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Zusatz-Handelsspanne',
        hash: 'zusatz-handelsspanne',
        parent: 'Angebot'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Service-Pauschale',
        hash: 'service-pauschale',
        parent: 'Angebot'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Angebot Endkunde Netto',
        hash: 'angebot-endkunde',
        parent: 'Angebot'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Angebot Partner Netto',
        hash: 'angebot-partner',
        parent: 'Angebot'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Angebot Wiederverkäufer Netto',
        hash: 'angebot-wiederverkaeufer',
        parent: 'Angebot'
      })

      // Börsendaten & Bilder - Konfiguration
      this.searchIndex.push({
        type: 'section',
        label: 'Konfiguration',
        hash: 'konfiguration',
        parent: 'Börsendaten & Bilder'
      })

      // Börsendaten & Bilder - Veröffentlichung
      this.searchIndex.push({
        type: 'section',
        label: 'Veröffentlichung',
        hash: 'veroeffentlichung',
        parent: 'Börsendaten & Bilder'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Veröffentlichen',
        hash: 'veroeffentlichen',
        parent: 'Veröffentlichung'
      })

      // Börsendaten & Bilder - Beschreibung
      this.searchIndex.push({
        type: 'section',
        label: 'Beschreibung',
        hash: 'beschreibung',
        parent: 'Börsendaten & Bilder'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Fahrzeugtitel',
        hash: 'fahrzeugtitel',
        parent: 'Beschreibung'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Fahrzeugbeschreibung',
        hash: 'fahrzeugbeschreibung',
        parent: 'Beschreibung'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Fahrzeugkategorie',
        hash: 'fahrzeugkategorie',
        parent: 'Beschreibung'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Fahrzeugart',
        hash: 'fahrzeugart',
        parent: 'Beschreibung'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Bisherige Nutzung',
        hash: 'bisherige-nutzung',
        parent: 'Beschreibung'
      })

      // Börsendaten & Bilder - Eckdaten
      this.searchIndex.push({
        type: 'section',
        label: 'Eckdaten',
        hash: 'eckdaten',
        parent: 'Börsendaten & Bilder'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Leergewicht',
        hash: 'leergewicht',
        parent: 'Eckdaten'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Batteriekapazität',
        hash: 'batteriekapazitaet',
        parent: 'Eckdaten'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Sitzplätze',
        hash: 'sitzplaetze',
        parent: 'Eckdaten'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Hubraum',
        hash: 'hubraum',
        parent: 'Eckdaten'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Getriebe',
        hash: 'getriebe',
        parent: 'Eckdaten'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Türen',
        hash: 'tueren',
        parent: 'Eckdaten'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Lieferzeit in Tagen',
        hash: 'lieferzeit',
        parent: 'Eckdaten'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Unfallfahrzeug',
        hash: 'unfallfahrzeug',
        parent: 'Eckdaten'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Nicht-reparierter Schaden',
        hash: 'nicht-reparierter-schaden',
        parent: 'Eckdaten'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Fahrtauglich',
        hash: 'fahrtauglich',
        parent: 'Eckdaten'
      })

      // Börsendaten & Bilder - Umweltdaten
      this.searchIndex.push({
        type: 'section',
        label: 'Umweltdaten',
        hash: 'umweltdaten',
        parent: 'Börsendaten & Bilder'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Kraftstoff',
        hash: 'kraftstoff',
        parent: 'Umweltdaten'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'CO2 Ausstoß NEFZ',
        hash: 'co2-nefz',
        parent: 'Umweltdaten'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'CO2 Ausstoß WLTP',
        hash: 'co2-wltp',
        parent: 'Umweltdaten'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Verbrauch innerorts',
        hash: 'verbrauch-innerorts',
        parent: 'Umweltdaten'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Verbrauch außerorts',
        hash: 'verbrauch-ausserorts',
        parent: 'Umweltdaten'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Verbrauch kombiniert',
        hash: 'verbrauch-kombiniert',
        parent: 'Umweltdaten'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Plakette',
        hash: 'plakette',
        parent: 'Umweltdaten'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Emissionsklasse',
        hash: 'emissionsklasse',
        parent: 'Umweltdaten'
      })

      // Börsendaten & Bilder - Ausstattung
      this.searchIndex.push({
        type: 'section',
        label: 'Ausstattung',
        hash: 'ausstattung',
        parent: 'Börsendaten & Bilder'
      })

      // Börsendaten & Bilder - Bilder
      this.searchIndex.push({
        type: 'section',
        label: 'Bilder',
        hash: 'bilder',
        parent: 'Börsendaten & Bilder'
      })

      // Preise
      this.searchIndex.push({
        type: 'page',
        label: 'Preise',
        link: 'preise',
        parent: null
      })

      // Preise - Unverbindliche Preisempfehlung
      this.searchIndex.push({
        type: 'section',
        label: 'Unverbindliche Preisempfehlung',
        hash: 'unverbindliche-preisempfehlung',
        parent: 'Preise'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'UPE Netto (ohne Garantie für Prämienberechnung)',
        hash: 'upe',
        parent: 'Unverbindliche Preisempfehlung'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'UPE zum Bestellzeitpunkt Netto',
        hash: 'upe-bestellzeitpunkt',
        parent: 'Unverbindliche Preisempfehlung'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'UPE aus Rechnung Netto',
        hash: 'upe-rechnung',
        parent: 'Unverbindliche Preisempfehlung'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Besteuerung',
        hash: 'besteuerung',
        parent: 'Unverbindliche Preisempfehlung'
      })

      // Preise - Einkauf
      this.searchIndex.push({
        type: 'section',
        label: 'Einkauf',
        hash: 'einkauf',
        parent: 'Preise'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Netto Einkaufspreis',
        hash: 'netto-einkaufspreis',
        parent: 'Einkauf'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'EK-Rechnungsnummer',
        hash: 'ek-rechnungsnummer',
        parent: 'Einkauf'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'EK-Rechnungsdatum',
        hash: 'ek-rechnungsdatum',
        parent: 'Einkauf'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'EK-Zahlungsziel',
        hash: 'ek-zahlungsziel',
        parent: 'Einkauf'
      })

      // Preise - Verkauf
      this.searchIndex.push({
        type: 'section',
        label: 'Verkauf',
        hash: 'verkauf',
        parent: 'Preise'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'VK-Rechnungsdatum',
        hash: 'vk-rechnungsdatum',
        parent: 'Verkauf'
      })

      // Prämien
      this.searchIndex.push({
        type: 'page',
        label: 'Prämien',
        link: 'praemien',
        parent: null
      })

      // Prämien - Prämien buchen
      this.searchIndex.push({
        type: 'section',
        label: 'Prämien buchen',
        hash: 'buchen',
        parent: 'Prämien'
      })

      // Prämien - Bereits gebuchte Prämienforderungen und erhaltene Zahlungen
      this.searchIndex.push({
        type: 'section',
        label: 'Bereits gebuchte Prämienforderungen und erhaltene Zahlungen',
        hash: 'bereits-gebuchte-praemienforderungen-und-erhaltene-zahlungen',
        parent: 'Prämien'
      })

      // Prämien - Subventionen
      this.searchIndex.push({
        type: 'section',
        label: 'Subventionen',
        hash: 'subventionen',
        parent: 'Prämien'
      })

      // Verkauf
      this.searchIndex.push({
        type: 'page',
        label: 'Verkauf',
        link: 'disponieren',
        parent: null
      })

      // Verkauf - Allgemein
      this.searchIndex.push({
        type: 'section',
        label: 'Allgemein',
        hash: 'verkauf-allgemein',
        parent: 'Verkauf'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Kunde',
        hash: 'kunde',
        parent: 'Allgemein'
      })

      // Verkauf - Auslieferung
      this.searchIndex.push({
        type: 'section',
        label: 'Auslieferung',
        hash: 'verkauf-auslieferung',
        parent: 'Verkauf'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Unverbindliche Auslieferung',
        hash: 'unverbindliche-auslieferung',
        parent: 'Auslieferung'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Terminierte Auslieferung',
        hash: 'terminierte-auslieferung',
        parent: 'Auslieferung'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Terminierte Uhrzeit',
        hash: 'terminierte-uhrzeit',
        parent: 'Auslieferung'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Auslieferer',
        hash: 'auslieferer',
        parent: 'Auslieferung'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Terminierte Zulassung',
        hash: 'terminierte-zulassung',
        parent: 'Auslieferung'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Zulassungsstelle',
        hash: 'zulassungsstelle',
        parent: 'Auslieferung'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Zulassung durch',
        hash: 'zulassung-durch',
        parent: 'Auslieferung'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Übergabeinspektion',
        hash: 'uebergabeinspektion',
        parent: 'Auslieferung'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Aufbereitung',
        hash: 'aufbereitung',
        parent: 'Auslieferung'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'Anmerkungen zur Auslieferung',
        hash: 'anmerkungen-zur-auslieferung',
        parent: 'Auslieferung'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'AK Meldung beim Hersteller',
        hash: 'ak-meldung-beim-hersteller',
        parent: 'Auslieferung'
      })

      // OPOS
      this.searchIndex.push({
        type: 'page',
        label: 'OPOS',
        link: 'opos',
        parent: null
      })

      // OPOS - Basisdaten
      this.searchIndex.push({
        type: 'section',
        label: 'Basisdaten',
        hash: 'basisdaten',
        parent: 'OPOS'
      })

      this.searchIndex.push({
        type: 'field',
        label: 'VK-Rechnungsdatum',
        hash: 'vk-rechnungsdatum',
        parent: 'Basisdaten'
      })

      // OPOS - Forderungen
      this.searchIndex.push({
        type: 'section',
        label: 'Forderungen',
        hash: 'forderungen',
        parent: 'OPOS'
      })

      // Kosten
      this.searchIndex.push({
        type: 'page',
        label: 'Kosten',
        link: 'kosten',
        parent: null
      })

      // Kosten - Kosten
      this.searchIndex.push({
        type: 'section',
        label: 'Kosten',
        hash: 'kosten',
        parent: 'Kosten'
      })

      this.createFuse()
    },

    createFuse() {
      this.fuse = new Fuse(this.searchIndex, {
        isCaseSensitive: false,
        findAllMatches: true,
        threshold: 0.3,
        keys: ['label']
      })
    },

    search() {
      if (this.query === '') {
        this.results = []
        return
      }

      const results = this.fuse.search(this.query).filter(result => result.type !== 'page')
      const sections = results.filter(result => result.type === 'section')

      results.filter(result => result.type === 'field').forEach((result) => {
        if (!sections.find(section => section.label === result.parent)) {
          sections.push(this.searchIndex.find(item => item.type === 'section' && item.label === result.parent))
        }
      })

      const pages = []

      sections.forEach((section) => {
        const sectionPage = this.searchIndex.filter(item => item.type === 'page').find(page => page.label === section.parent)

        if (!pages.find(p => p.label === sectionPage.label)) {
          pages.push(sectionPage)
        }
      })

      this.results = pages.map(page => ({
        ...page,
        children: sections.filter(section => section.parent === page.label).map(section => ({ ...section, children: results.filter(res => res.type === 'field').filter(res => res.parent === section.label) }))
      }))
    },

    reset() {
      this.query = ''
      this.results = []
    },

    onFocus() {
      this.$el.querySelector('input').select()
    }
  }
}
</script>
